<script setup lang="ts">
  import { ref, computed, inject, onMounted } from 'vue';
  import useTotpStore from '@/common/store/pinia/totp';
  import useAuthStore from '@/common/store/pinia/auth';
  import TotpVerification from '@/common/shared/TotpVerification.vue';
  import RestoreAccessInfoDialog from '@/common/components/user/RestoreAccessInfoDialog.vue';
  import { useNotifications } from '@/common/utils/notifications/notificationsPlugin';
  import { useI18n } from '@/common/composables/vue-i18n.composable';
  import NoInjectError from '@/common/utils/errors/NoInjectError';
  import CancellationError from '@/common/utils/errors/CancellationError';

  type EmitEvents = (event: 'close') => void;

  type Props = {
    logoutAfterDisable: boolean;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<EmitEvents>();
  const totpStore = useTotpStore();
  const authStore = useAuthStore();
  const { notify } = useNotifications();
  const { t } = useI18n();
  const globalConfirm = inject<() => TypeConfirm>('globalConfirm');

  if (!globalConfirm) throw new NoInjectError();

  const verificationRef = ref();
  const restoreAccessInfoDialogRef = ref<InstanceType<typeof RestoreAccessInfoDialog>>();
  const formError = ref('');

  const pending = computed(() => totpStore.isPending);
  const username = computed(() => authStore.currentUser?.name || '');
  const isSystemAdmin = computed(() => authStore.isSystemAdmin);

  const onClose = () => {
    verificationRef.value?.resetForm();
    totpStore.clearError();
    formError.value = '';
    emit('close');
  };

  const clearError = () => {
    formError.value = '';
    totpStore.clearError();
  };

  const handleSubmit = async (code: string) => {
    formError.value = '';
    return totpStore.disableTotp(code)
      .then(() => {
        notify.success({
          skipStore: true,
          title: t('Two-factor authentication reset'),
          message: t('Two-factor authentication successfully reset')
        });

        if (props.logoutAfterDisable) {
          authStore.signOut();
        } else {
          onClose();
        }
      })
      .catch((err: any) => {
        formError.value = err?.message || t('Invalid verification code');
      });
  };

  const handleAdminSubmit = async () => globalConfirm().create({
    title: t('Reset two-factor authentication'),
    content: t('Are you sure that you want to reset two-factor authentication?'),
    buttonConfirm: t('Reset'),
    type: 'danger',
    action: () => totpStore.disableTotp()
      .then(() => {
        notify.success({
          skipStore: true,
          title: t('Two-factor authentication reset'),
          message: t('Two-factor authentication successfully reset')
        });
        if (props.logoutAfterDisable) {
          authStore.signOut();
        } else {
          onClose();
        }
      })
  }).catch((err) => {
    if (err instanceof CancellationError) {
      onClose();
      return;
    }
    throw err;
  });

  const handleCancel = () => {
    onClose();
  };

  const handleHelpRequested = () => {
    restoreAccessInfoDialogRef.value?.create();
  };

  onMounted(() => {
    if (isSystemAdmin.value) {
      handleAdminSubmit();
    }
  });

  defineExpose({
    handleSubmit,
    onClose,
    clearError,
    handleCancel,
    handleHelpRequested,
    handleAdminSubmit,
    verificationRef,
    restoreAccessInfoDialogRef,
    formError,
    totpStore,
    authStore
  });
</script>

<template>
<el-dialog data-component-name="disable-two-factor-auth-dialog"
  v-if="!isSystemAdmin"
  append-to-body
  :custom-class="'disable-two-factor-auth-dialog'"
  height-size="fixed-auto"
  visible
  width-size="small"
  :show-close="false"
  @close="onClose"
>
  <template #body-header>
    <div class="body-header-title el-text--align-center pl-24 pt-24 pr-24 pb-16">
      {{ $t('Reset two-factor authentication') }}
    </div>
  </template>

  <div class="dialog-body pl-24 pr-24 pb-24">
    <div class="username mb-24">
      {{ username }}
    </div>

    <TotpVerification data-component-name="totp-verification"
      ref="verificationRef"
      :error="formError"
      :pending="pending"
      :show-back-button="true"
      :show-header="false"
      :submit-button-text="t('Reset')"
      :cancel-button-text="t('Cancel')"
      @cancel="handleCancel"
      @submit="handleSubmit"
      @input="clearError"
      @helpRequested="handleHelpRequested"
    />
  </div>
  <RestoreAccessInfoDialog data-component-name="restore-access-info-dialog" ref="restoreAccessInfoDialogRef" />
</el-dialog>
</template>

<style lang="scss" scoped>
::v-deep .disable-two-factor-auth-dialog .el-dialog__body {
  background-color: $av-fixed-white;
}

.body-header-title {
  font-size: $av-font-size-l;
}

.username {
  font-size: $av-font-size-xs;
  text-align: center;
  color: $av-fixed-light;
}
</style>
